/* eslint-disable @typescript-eslint/no-unused-vars */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, expand, reduce, tap } from 'rxjs';

import { GBE_BASE_URL } from '@gfe/core/tokens/gbe-url.token';
import { FilterConfiguration } from '@gfe/shared/components/filter-query-builder/filter.models';
import { convertFilterConfigurationToBackEndFormat } from '@gfe/shared/utils/search-filter.utils';

import {
  SKIP_GBE_ERROR_NOTIFICATION,
  SKIP_GBE_ERROR_RETRY
} from '@core/interceptors/gbe-error.interceptor';
import { OrderBy } from '@core/models/entity/entity-selector-request.model';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { PlanMetadataModel } from '@core/models/plan.model';
import {
  AddPlanningSpaceRequest,
  AddPlanningSpaceResponse,
  PlanningSpaceMetadataModel,
  UpdatePlanningSpaceRequest,
  UpdatePlanningSpaceResponse
} from '@core/models/planning-space/planning-space.model';

export interface FetchPlanningSpaceListRequest {
  take?: number;
  page?: number;
  dataSpaceId: number;
  search?: string;
  filters?: FilterConfiguration;
  /**
   * Keyed by DB column name, not by table column key. The list is paged server-side, so
   * ordering has to happen there too: sorting the page the client already holds would only
   * reorder 25 arbitrary rows.
   */
  orderBy?: OrderBy[];
  /**
   * Leaves the spaces marked internal out of the result set. Deliberately not a
   * `filters` condition: the marker is a visibility mechanism, not a business attribute,
   * and inside `filters` it would surface in the filter-builder UI and get saved into
   * shared filter presets. GBE applies it before paging, so `total` stays honest.
   */
  hideInternal?: boolean;
}

export interface FetchPlanningSpaceListResponse {
  total: number;
  page: number;
  result: PlanningSpaceMetadataModel[];
}

export interface FetchPlansForOnePlanningSpaceResponse {
  total: number;
  page: number;
  take: number;
  result: PlanMetadataModel[];
}

export interface FetchOnePlanningSpaceRequest {
  planningSpaceId: string;
}
export interface FetchAllPlanningSpaceRequest {
  planningSpaceId: string;
  take?: number;
  page?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PlanningSpaceService {
  private readonly http = inject(HttpClient);
  private readonly gbeUrl = inject(GBE_BASE_URL);

  private buildUrl(endpoint: string): string {
    return `${this.gbeUrl}/${endpoint}`;
  }

  constructor() {}

  public fetchList$(
    request: FetchPlanningSpaceListRequest
  ): Observable<FetchPlanningSpaceListResponse> {
    const url = this.buildUrl(`${GbeEndPointV4.PlANNINGSPACES}/search`);
    return this.http.post<FetchPlanningSpaceListResponse>(
      url,
      {
        filters: convertFilterConfigurationToBackEndFormat(request.filters),
        ...(request.orderBy?.length ? { orderBy: request.orderBy } : {})
      },
      {
        params: {
          take: request.take?.toString() || '25',
          page: request.page?.toString() || '1',
          dataSpaceId: request.dataSpaceId,
          ...(request.search ? { search: request.search } : {}),
          // Sent only to ask for the exclusion; omitted rather than sent as `false`,
          // which GBE treats identically to an absent parameter.
          ...(request.hideInternal ? { hide_internal: true } : {})
        }
      }
    );
  }

  public fetchAllPLanForOnePlanningSpace$(
    request: FetchAllPlanningSpaceRequest
  ): Observable<FetchPlansForOnePlanningSpaceResponse> {
    const url =
      this.buildUrl(GbeEndPointV4.PlANNINGSPACES) + `/${request.planningSpaceId}/plans`;
    return this.http.get<FetchPlansForOnePlanningSpaceResponse>(url, {
      params: {
        take: request.take?.toString() || '100',
        page: request.page?.toString() || '1'
      }
    });
  }

  /**
   * Every plan of a space, walking the pages: the endpoint caps a page, and a caller offering
   * the plans as choices must not silently lose the ones past the first page.
   */
  public fetchEveryPlanOfPlanningSpace$(
    planningSpaceId: string
  ): Observable<PlanMetadataModel[]> {
    const take = 100;
    const fetchPage = (page: number) =>
      this.fetchAllPLanForOnePlanningSpace$({ planningSpaceId, take, page });
    return fetchPage(1).pipe(
      // An empty page ends the walk too, so a `total` the pages never reach cannot loop.
      expand((response) =>
        response.result.length > 0 && response.page * take < response.total
          ? fetchPage(response.page + 1)
          : EMPTY
      ),
      reduce((plans, response) => [...plans, ...response.result], [] as PlanMetadataModel[])
    );
  }

  public addPlanningSpace$(
    request: AddPlanningSpaceRequest
  ): Observable<AddPlanningSpaceResponse> {
    const url = this.buildUrl(GbeEndPointV4.PLANS);
    return this.http.post<AddPlanningSpaceResponse>(url, request);
  }

  public updatePlanningSpace$(
    planningSpaceId: string,
    request: UpdatePlanningSpaceRequest
  ): Observable<UpdatePlanningSpaceResponse> {
    const url = this.buildUrl(GbeEndPointV4.PlANNINGSPACES) + `/${planningSpaceId}`;
    return this.http.put<UpdatePlanningSpaceResponse>(url, request);
  }

  public fetchOne$(
    request: FetchOnePlanningSpaceRequest
  ): Observable<PlanningSpaceMetadataModel> {
    const url = this.buildUrl(GbeEndPointV4.PlANNINGSPACES) + `/${request.planningSpaceId}`;
    return this.http.get<PlanningSpaceMetadataModel>(url);
  }

  /**
   * Reserved to the internal roles. Clearing the marker always succeeds; setting it is
   * refused unless `canBeMadeInternal` on the space says GBE would accept it.
   */
  public setPlanningSpaceInternal$(
    planningSpaceId: string,
    isInternal: boolean
  ): Observable<void> {
    const url =
      this.buildUrl(GbeEndPointV4.PlANNINGSPACES) + `/${planningSpaceId}/internal`;
    return this.http.put<void>(url, { isInternal });
  }

  /**
   * Retires the space and, through the data layer, every plan in it — deployed plans
   * included. It can be refused while any of those plans is being optimized, so the
   * generic notification is off: the caller deletes several spaces in one action and
   * owns the copy that says which one was refused and why.
   *
   * Retries are off because the call is not idempotent and each attempt runs a data
   * layer sweep inside a transaction of up to 30 s — a re-sent delete would report a
   * failure for a space the first attempt had already removed.
   */
  public deletePlanningSpace$(planningSpaceId: string): Observable<void> {
    const url = `${this.buildUrl(GbeEndPointV4.PlANNINGSPACES)}/${planningSpaceId}`;
    return this.http.delete<void>(url, {
      context: new HttpContext()
        .set(SKIP_GBE_ERROR_NOTIFICATION, true)
        .set(SKIP_GBE_ERROR_RETRY, true)
    });
  }
}
